import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, ChevronDownIcon, ChevronUpIcon, FilterIcon, XIcon, GlobeIcon, BuildingIcon } from 'lucide-react';
import PublicAssessmentCard from './PublicAssessmentCard';
import PreviewModal from './PreviewModal';
import EmptyState from './EmptyState';
import { AssignmentsPanel, Assignment } from './AssignmentsPanel';
interface PublicAssessmentsProps {
  userHasOrganization?: boolean; // Whether user is linked to an organization
}
const PublicAssessments: React.FC<PublicAssessmentsProps> = ({
  userHasOrganization = true // Default to true for demo
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [previewAssessment, setPreviewAssessment] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [subjectInputValue, setSubjectInputValue] = useState('');
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [selectedGradeLevel, setSelectedGradeLevel] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'popular' | 'recent' | 'title'>('popular');
  const [savedAssessmentIds, setSavedAssessmentIds] = useState<Set<number>>(new Set());
  // Tab state for Public vs Organization assessments
  const [activeTab, setActiveTab] = useState<'public' | 'organization'>('public');
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const subjectDropdownRef = useRef<HTMLDivElement>(null);
  const [assignmentsPanelAssessment, setAssignmentsPanelAssessment] = useState<any>(null);
  const [autoCreateNew, setAutoCreateNew] = useState(false);
  const [assessmentAssignments, setAssessmentAssignments] = useState<Record<number, Assignment[]>>({});
  // Load saved assessments from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('savedAssessments');
    if (saved) {
      setSavedAssessmentIds(new Set(JSON.parse(saved)));
    }
  }, []);
  // Save to localStorage whenever savedAssessmentIds changes
  useEffect(() => {
    localStorage.setItem('savedAssessments', JSON.stringify(Array.from(savedAssessmentIds)));
  }, [savedAssessmentIds]);
  const handleToggleSave = (assessmentId: number) => {
    setSavedAssessmentIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assessmentId)) {
        newSet.delete(assessmentId);
      } else {
        newSet.add(assessmentId);
      }
      return newSet;
    });
  };
  const handleUpdateAssignments = (assessmentId: number, assignments: Assignment[]) => {
    setAssessmentAssignments(prev => ({
      ...prev,
      [assessmentId]: assignments
    }));
    if (assignmentsPanelAssessment?.id === assessmentId) {
      setAssignmentsPanelAssessment((prev: any) => prev ? {
        ...prev,
        assignments
      } : prev);
    }
  };
  // All subjects organized by category
  const allSubjects = [
  // Core Subjects (Popular)
  {
    id: 'mathematics',
    label: 'Mathematics',
    category: 'Core Subjects'
  }, {
    id: 'science',
    label: 'Science',
    category: 'Core Subjects'
  }, {
    id: 'english',
    label: 'English',
    category: 'Core Subjects'
  }, {
    id: 'social-studies',
    label: 'Social Studies',
    category: 'Core Subjects'
  },
  // Sciences
  {
    id: 'physics',
    label: 'Physics',
    category: 'Sciences'
  }, {
    id: 'chemistry',
    label: 'Chemistry',
    category: 'Sciences'
  }, {
    id: 'biology',
    label: 'Biology',
    category: 'Sciences'
  }, {
    id: 'environmental-science',
    label: 'Environmental Science',
    category: 'Sciences'
  },
  // Social Sciences
  {
    id: 'history',
    label: 'History',
    category: 'Social Sciences'
  }, {
    id: 'geography',
    label: 'Geography',
    category: 'Social Sciences'
  }, {
    id: 'civics',
    label: 'Civics / Political Science',
    category: 'Social Sciences'
  }, {
    id: 'psychology',
    label: 'Psychology',
    category: 'Social Sciences'
  }, {
    id: 'sociology',
    label: 'Sociology',
    category: 'Social Sciences'
  }, {
    id: 'philosophy',
    label: 'Philosophy',
    category: 'Social Sciences'
  },
  // Technology
  {
    id: 'computer-science',
    label: 'Computer Science / ICT',
    category: 'Technology'
  }, {
    id: 'coding',
    label: 'Coding',
    category: 'Technology'
  }, {
    id: 'ai',
    label: 'Artificial Intelligence',
    category: 'Technology'
  },
  // Business & Economics
  {
    id: 'economics',
    label: 'Economics',
    category: 'Business & Economics'
  }, {
    id: 'business-studies',
    label: 'Business Studies',
    category: 'Business & Economics'
  }, {
    id: 'accountancy',
    label: 'Accountancy',
    category: 'Business & Economics'
  }, {
    id: 'financial-literacy',
    label: 'Financial Literacy',
    category: 'Business & Economics'
  }, {
    id: 'entrepreneurship',
    label: 'Entrepreneurship',
    category: 'Business & Economics'
  },
  // Arts & Physical Education
  {
    id: 'art',
    label: 'Art & Craft',
    category: 'Arts & PE'
  }, {
    id: 'music',
    label: 'Music',
    category: 'Arts & PE'
  }, {
    id: 'physical-education',
    label: 'Physical Education',
    category: 'Arts & PE'
  },
  // Languages
  {
    id: 'hindi',
    label: 'Hindi',
    category: 'Languages'
  }, {
    id: 'sanskrit',
    label: 'Sanskrit',
    category: 'Languages'
  }, {
    id: 'french',
    label: 'French',
    category: 'Languages'
  }, {
    id: 'spanish',
    label: 'Spanish',
    category: 'Languages'
  }, {
    id: 'urdu',
    label: 'Urdu',
    category: 'Languages'
  }, {
    id: 'telugu',
    label: 'Telugu',
    category: 'Languages'
  }, {
    id: 'tamil',
    label: 'Tamil',
    category: 'Languages'
  }, {
    id: 'marathi',
    label: 'Marathi',
    category: 'Languages'
  },
  // Other
  {
    id: 'life-skills',
    label: 'Life Skills',
    category: 'Other'
  }];
  // Popular subjects to show when input is empty
  const popularSubjects = ['Mathematics', 'Science', 'English', 'History', 'Computer Science / ICT', 'Physics', 'Chemistry', 'Biology'];
  // Grade level filter options
  const gradeLevelFilters = [{
    id: 'all',
    label: 'All Grades'
  }, {
    id: 'early-years',
    label: 'Early Years'
  }, {
    id: 'g1-3',
    label: 'G1–3'
  }, {
    id: 'g4-5',
    label: 'G4–5'
  }, {
    id: 'g6-8',
    label: 'G6–8'
  }, {
    id: 'g9-10',
    label: 'G9–10'
  }, {
    id: 'g11-12',
    label: 'G11–12'
  }, {
    id: 'undergraduate',
    label: 'Undergraduate'
  }, {
    id: 'professional',
    label: 'Professional'
  }];
  // All assessments with visibility property
  const allAssessments = [{
    id: 1,
    title: 'Math Fundamentals: Fractions',
    tags: ['Math', 'Elementary', 'Practice'],
    mode: 'Challenge',
    creator: 'System',
    attempts: 1245,
    uses: 1245,
    icon: '🔢',
    gradeLevel: 'g4-5',
    subject: 'mathematics',
    visibility: 'public',
    description: 'Master the basics of fractions including addition, subtraction, multiplication, and division.',
    questions: 15,
    duration: '20 min',
    learningObjectives: 'Students will understand fraction operations, simplify fractions, and convert between fractions and decimals.',
    topicsCovered: 'Addition and subtraction of fractions, multiplication and division, simplification, decimal conversion, and real-world applications.',
    curriculum: 'Common Core Math Standards 4.NF, 5.NF',
    questionsList: [{
      id: 1,
      text: 'What is 1/2 + 1/4?',
      type: 'Multiple Choice',
      difficulty: 'Easy',
      answer: '3/4'
    }, {
      id: 2,
      text: 'Simplify 8/12 to its lowest terms',
      type: 'Multiple Choice',
      difficulty: 'Easy',
      answer: '2/3'
    }, {
      id: 3,
      text: 'What is 3/4 - 1/3?',
      type: 'Multiple Choice',
      difficulty: 'Medium',
      answer: '5/12'
    }]
  }, {
    id: 2,
    title: 'Science: States of Matter',
    tags: ['Science', 'Physics'],
    mode: 'Coach',
    creator: 'Jane Smith',
    attempts: 876,
    uses: 876,
    icon: '🧪',
    gradeLevel: 'g6-8',
    subject: 'science',
    visibility: 'organization',
    organizationName: 'Springfield High School',
    description: 'Explore the three states of matter and their properties through interactive questions.',
    questions: 12,
    duration: '15 min',
    learningObjectives: 'Students will identify properties of solids, liquids, and gases, and understand phase transitions.',
    topicsCovered: 'States of matter characteristics, particle behavior, phase changes (melting, freezing, evaporation, condensation), and temperature effects.',
    curriculum: 'NGSS MS-PS1-4, State Science Standards',
    questionsList: [{
      id: 1,
      text: 'Name the three main states of matter',
      type: 'Multiple Choice',
      difficulty: 'Easy',
      answer: 'Solid, Liquid, Gas'
    }]
  }, {
    id: 3,
    title: 'English: Grammar Basics',
    tags: ['English', 'Grammar'],
    mode: 'Challenge',
    creator: 'System',
    attempts: 1543,
    uses: 1543,
    icon: '📝',
    gradeLevel: 'g6-8',
    subject: 'english',
    visibility: 'public',
    description: 'Test your knowledge of grammar fundamentals including parts of speech and sentence structure.',
    questions: 20,
    duration: '25 min',
    learningObjectives: 'Students will identify parts of speech, construct proper sentences, and apply grammar rules correctly.',
    topicsCovered: 'Nouns, verbs, adjectives, adverbs, pronouns, prepositions, conjunctions, sentence structure, and punctuation.',
    curriculum: 'Common Core ELA Standards L.6-8',
    questionsList: []
  }, {
    id: 4,
    title: 'History: Ancient Civilizations',
    tags: ['History', 'World'],
    mode: 'Coach',
    creator: 'John Doe',
    attempts: 654,
    uses: 654,
    icon: '🏛️',
    gradeLevel: 'g6-8',
    subject: 'history',
    visibility: 'public',
    description: 'Journey through ancient civilizations and learn about their cultures and achievements.',
    questions: 18,
    duration: '30 min',
    learningObjectives: 'Students will identify key historical figures, civilizations, and cultural achievements of ancient times.',
    topicsCovered: 'Ancient Mesopotamia, Egypt, Greece, Rome, and other early civilizations, their contributions, and historical significance.',
    curriculum: 'Common Core History Standards SS.7-9',
    questionsList: []
  }, {
    id: 5,
    title: 'Geography: Continents and Oceans',
    tags: ['Geography'],
    mode: 'Challenge',
    creator: 'System',
    attempts: 987,
    uses: 987,
    icon: '🌍',
    gradeLevel: 'g4-5',
    subject: 'geography',
    visibility: 'public',
    description: "Learn about the world's continents and oceans with this comprehensive assessment.",
    questions: 10,
    duration: '15 min',
    learningObjectives: 'Students will identify continents, oceans, and major geographical features, and understand global patterns.',
    topicsCovered: 'Continental and oceanic boundaries, major landforms, climate zones, and global geographical relationships.',
    curriculum: 'Common Core Geography Standards G.4-6',
    questionsList: []
  }, {
    id: 6,
    title: 'Art History: Renaissance Period',
    tags: ['Art', 'History', 'Culture', 'Renaissance'],
    mode: 'Coach',
    creator: 'Sarah Johnson',
    attempts: 432,
    uses: 432,
    icon: '🎨',
    gradeLevel: 'g9-10',
    subject: 'art',
    visibility: 'public',
    description: 'Discover the masterpieces and artists of the Renaissance period.',
    questions: 14,
    duration: '20 min',
    learningObjectives: 'Students will identify key Renaissance artists, artworks, and understand the cultural and artistic movements of the period.',
    topicsCovered: 'Renaissance art characteristics, major artists (Leonardo, Michelangelo, Raphael), famous works, and the humanist philosophy that influenced the era.',
    curriculum: 'Common Core Art History Standards AH.8-10',
    questionsList: []
  }, {
    id: 7,
    title: 'Music Theory: Basics',
    tags: ['Music'],
    mode: 'Challenge',
    creator: 'System',
    attempts: 765,
    uses: 765,
    icon: '🎵',
    gradeLevel: 'g6-8',
    subject: 'music',
    visibility: 'public',
    description: 'Learn the fundamentals of music theory including notes, scales, and rhythms.',
    questions: 16,
    duration: '20 min',
    learningObjectives: 'Students will understand musical notation, scales, intervals, and basic rhythm patterns.',
    topicsCovered: 'Musical notes, scales, intervals, time signatures, rhythm, and basic music theory concepts.',
    curriculum: 'Common Core Music Standards M.5-7',
    questionsList: []
  }, {
    id: 8,
    title: 'Computer Science: Algorithms',
    tags: ['Computer Science', 'Programming'],
    mode: 'Coach',
    creator: 'Michael Chen',
    attempts: 543,
    uses: 543,
    icon: '💻',
    gradeLevel: 'g11-12',
    subject: 'computer-science',
    visibility: 'organization',
    organizationName: 'Springfield High School',
    description: 'Understand basic algorithms and their applications in computer science.',
    questions: 12,
    duration: '25 min',
    learningObjectives: 'Students will understand algorithmic thinking, basic programming concepts, and problem-solving strategies.',
    topicsCovered: 'Algorithms, pseudocode, loops, conditionals, data structures, and problem-solving approaches.',
    curriculum: 'Computer Science Principles (CS1)',
    questionsList: []
  }, {
    id: 9,
    title: 'Spanish: Conversational Basics',
    tags: ['Spanish', 'Language'],
    mode: 'Coach',
    creator: 'Maria Garcia',
    attempts: 892,
    uses: 892,
    icon: '🇪🇸',
    gradeLevel: 'g6-8',
    subject: 'spanish',
    visibility: 'public',
    description: 'Learn essential Spanish phrases and conversational skills for everyday situations.',
    questions: 18,
    duration: '25 min',
    learningObjectives: 'Students will learn basic Spanish greetings, common phrases, and simple conversation patterns.',
    topicsCovered: 'Greetings, introductions, numbers, colors, basic verbs, and everyday vocabulary.',
    curriculum: 'World Language Standards',
    questionsList: []
  }, {
    id: 10,
    title: 'French: Grammar Fundamentals',
    tags: ['French', 'Language'],
    mode: 'Challenge',
    creator: 'Pierre Dubois',
    attempts: 654,
    uses: 654,
    icon: '🇫🇷',
    gradeLevel: 'g11-12',
    subject: 'french',
    visibility: 'public',
    description: 'Master the basics of French grammar including verb conjugations and sentence structure.',
    questions: 15,
    duration: '20 min',
    learningObjectives: 'Students will understand French verb conjugations, gender agreement, and basic sentence construction.',
    topicsCovered: 'Present tense verbs, articles, gender, plurals, and basic sentence patterns.',
    curriculum: 'World Language Standards',
    questionsList: []
  }, {
    id: 11,
    title: 'Biology: Cell Structure',
    tags: ['Biology', 'Science'],
    mode: 'Coach',
    creator: 'Dr. Emily Watson',
    attempts: 723,
    uses: 723,
    icon: '🔬',
    gradeLevel: 'g9-10',
    subject: 'biology',
    visibility: 'organization',
    organizationName: 'Springfield High School',
    description: 'Explore the structure and function of cells, the basic units of life.',
    questions: 14,
    duration: '20 min',
    learningObjectives: 'Students will identify cell organelles, understand their functions, and compare plant and animal cells.',
    topicsCovered: 'Cell membrane, nucleus, mitochondria, chloroplasts, cell wall, and organelle functions.',
    curriculum: 'NGSS HS-LS1-2',
    questionsList: []
  }, {
    id: 12,
    title: 'Chemistry: Periodic Table',
    tags: ['Chemistry', 'Science'],
    mode: 'Challenge',
    creator: 'Prof. David Lee',
    attempts: 589,
    uses: 589,
    icon: '⚗️',
    gradeLevel: 'g9-10',
    subject: 'chemistry',
    visibility: 'organization',
    organizationName: 'Springfield High School',
    description: 'Master the periodic table, element properties, and chemical trends.',
    questions: 16,
    duration: '25 min',
    learningObjectives: 'Students will navigate the periodic table, identify element groups, and understand periodic trends.',
    topicsCovered: 'Element symbols, atomic structure, groups and periods, metals/nonmetals, and periodic trends.',
    curriculum: 'NGSS HS-PS1-1',
    questionsList: []
  }];
  // Filter assessments by visibility (public or organization)
  const assessmentsByVisibility = allAssessments.filter(assessment => {
    if (activeTab === 'public') {
      return assessment.visibility === 'public';
    } else {
      return assessment.visibility === 'organization';
    }
  });
  // Filter subjects based on input
  const filteredSubjects = subjectInputValue ? allSubjects.filter(subject => subject.label.toLowerCase().includes(subjectInputValue.toLowerCase())) : allSubjects.filter(subject => popularSubjects.includes(subject.label));
  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (subjectDropdownRef.current && !subjectDropdownRef.current.contains(event.target as Node) && subjectInputRef.current && !subjectInputRef.current.contains(event.target as Node)) {
        setShowSubjectDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Filter and sort assessments
  const filteredAssessments = assessmentsByVisibility.filter(assessment => {
    // Subject filter
    if (selectedSubject) {
      if (assessment.subject !== selectedSubject) return false;
    }
    // Grade level filter
    if (selectedGradeLevel !== 'all') {
      if (assessment.gradeLevel !== selectedGradeLevel) return false;
    }
    // Search filter
    if (searchQuery === '') return true;
    return assessment.title.toLowerCase().includes(searchQuery.toLowerCase()) || assessment.creator.toLowerCase().includes(searchQuery.toLowerCase()) || assessment.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
  }).sort((a, b) => {
    switch (sortBy) {
      case 'popular':
        return (b.uses || 0) - (a.uses || 0);
      case 'recent':
        return b.id - a.id;
      case 'title':
        return a.title.localeCompare(b.title);
      default:
        return 0;
    }
  });
  const handleSubjectSelect = (subjectId: string, subjectLabel: string) => {
    setSelectedSubject(subjectId);
    setSubjectInputValue(subjectLabel);
    setShowSubjectDropdown(false);
  };
  const handleClearSubject = () => {
    setSelectedSubject('');
    setSubjectInputValue('');
    subjectInputRef.current?.focus();
  };
  const getActiveFiltersText = () => {
    const filters = [];
    if (selectedSubject) {
      const subject = allSubjects.find(s => s.id === selectedSubject);
      if (subject) filters.push(subject.label);
    }
    if (selectedGradeLevel !== 'all') {
      filters.push(gradeLevelFilters.find(g => g.id === selectedGradeLevel)?.label);
    }
    return filters.length > 0 ? ` • Filtered by: ${filters.join(', ')}` : '';
  };
  // Count assessments by visibility
  const publicCount = allAssessments.filter(a => a.visibility === 'public').length;
  const organizationCount = allAssessments.filter(a => a.visibility === 'organization').length;
  return <section className="mb-20" data-id="element-2012">
      {/* Collapsible Header */}
      <button onClick={() => setIsExpanded(!isExpanded)} className="w-full flex items-center justify-between mb-6 group" data-id="element-2013">
        <div className="flex items-center gap-3" data-id="element-2014">
          <h2 className="text-2xl font-bold text-text-primary" data-id="element-2015">
            Browse Assessments
          </h2>
          <span className="text-sm text-text-secondary bg-surface px-3 py-1 rounded-full" data-id="element-2016">
            {allAssessments.length} available
          </span>
        </div>
        <div className="flex items-center gap-2 text-text-secondary group-hover:text-text-primary transition-colors" data-id="element-2017">
          <span className="text-sm font-medium" data-id="element-2018">
            {isExpanded ? 'Collapse' : 'Expand'}
          </span>
          {isExpanded ? <ChevronUpIcon size={20} data-id="element-2019" /> : <ChevronDownIcon size={20} data-id="element-2020" />}
        </div>
      </button>

      {/* Collapsible Content */}
      <AnimatePresence data-id="element-2021">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-2022">
            {/* Tabs - Only show if user has organization */}
            {userHasOrganization && <div className="mb-6" data-id="element-2023">
                {/* Mobile: Dropdown selector */}
                <div className="block sm:hidden" data-id="element-2024">
                  <select value={activeTab} onChange={e => setActiveTab(e.target.value as 'public' | 'organization')} className="w-full px-4 py-3 bg-surface border-2 border-custom rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-[#00B75F] text-text-primary appearance-none cursor-pointer" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M6 8L2 4h8z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 1rem center'
            }} data-id="element-2025">
                    <option value="public" data-id="element-2026">🌐 Public</option>
                    <option value="organization" data-id="element-2027">🏢 Within Organization</option>
                  </select>
                </div>

                {/* Desktop: Tab buttons */}
                <div className="hidden sm:flex gap-2 border-b border-custom" data-id="element-2028">
                  <button onClick={() => setActiveTab('public')} className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-all relative ${activeTab === 'public' ? 'text-[#00B75F]' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-2029">
                    <GlobeIcon size={16} data-id="element-2030" />
                    <span data-id="element-2031">Public</span>
                    {activeTab === 'public' && <motion.div layoutId="assessmentTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00B75F]" transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} data-id="element-2032" />}
                  </button>
                  <button onClick={() => setActiveTab('organization')} className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-all relative ${activeTab === 'organization' ? 'text-[#00B75F]' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-2033">
                    <BuildingIcon size={16} data-id="element-2034" />
                    <span data-id="element-2035">Within Organization</span>
                    {activeTab === 'organization' && <motion.div layoutId="assessmentTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00B75F]" transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} data-id="element-2036" />}
                  </button>
                </div>
              </div>}

            {/* Filters Section */}
            <div className="space-y-4 mb-6" data-id="element-2037">
              {/* Filter Row */}
              <div className="flex flex-col lg:flex-row gap-3" data-id="element-2038">
                {/* Grade Level Filter Dropdown */}
                <div className="relative flex-1 lg:flex-initial lg:w-48" data-id="element-2039">
                  <FilterIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary pointer-events-none z-10" data-id="element-2040" />
                  <select value={selectedGradeLevel} onChange={e => setSelectedGradeLevel(e.target.value)} className="w-full pl-9 pr-8 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-[#00B75F] text-text-primary appearance-none cursor-pointer hover:border-[#00B75F] hover:border-opacity-50 transition-all shadow-sm" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M6 8L2 4h8z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center'
              }} data-id="element-2041">
                    {gradeLevelFilters.map(level => <option key={level.id} value={level.id} data-id="element-2042">
                        {level.label}
                      </option>)}
                  </select>
                </div>

                {/* Subject Searchable Input */}
                <div className="relative flex-1 lg:flex-initial lg:w-64" ref={subjectDropdownRef} data-id="element-2043">
                  <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary pointer-events-none z-10" data-id="element-2044" />
                  <input ref={subjectInputRef} type="text" placeholder="Search subjects..." value={subjectInputValue} onChange={e => setSubjectInputValue(e.target.value)} onFocus={() => setShowSubjectDropdown(true)} className="w-full pl-9 pr-9 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-[#00B75F] text-text-primary placeholder-text-tertiary hover:border-[#00B75F] hover:border-opacity-50 transition-all shadow-sm" data-id="element-2045" />
                  {selectedSubject && <button onClick={handleClearSubject} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-tertiary hover:text-text-primary transition-colors z-10 p-1 rounded-md hover:bg-surface-elevated" aria-label="Clear subject" data-id="element-2046">
                      <XIcon size={16} data-id="element-2047" />
                    </button>}

                  {/* Subject Dropdown */}
                  {showSubjectDropdown && <div className="absolute top-full left-0 right-0 mt-2 bg-surface-elevated border-2 border-custom rounded-xl shadow-custom-lg max-h-80 overflow-hidden z-50" data-id="element-2048">
                      {/* Header */}
                      <div className="sticky top-0 bg-surface-elevated border-b-2 border-custom px-4 py-3" data-id="element-2049">
                        <p className="text-xs font-bold text-text-secondary uppercase tracking-wider" data-id="element-2050">
                          {subjectInputValue ? 'Search Results' : 'Popular Subjects'}
                        </p>
                      </div>

                      {/* Subject List */}
                      <div className="overflow-y-auto max-h-64" data-id="element-2051">
                        {filteredSubjects.length > 0 ? <div className="py-1" data-id="element-2052">
                            {filteredSubjects.map(subject => <button key={subject.id} onClick={() => handleSubjectSelect(subject.id, subject.label)} className={`w-full text-left px-4 py-2.5 hover:bg-surface transition-colors ${selectedSubject === subject.id ? 'bg-[#00B75F] bg-opacity-10 text-[#00B75F] font-semibold' : 'text-text-primary'}`} data-id="element-2053">
                                <div className="flex items-center justify-between" data-id="element-2054">
                                  <span className="text-sm font-medium" data-id="element-2055">
                                    {subject.label}
                                  </span>
                                  <span className="text-xs text-text-tertiary font-medium" data-id="element-2056">
                                    {subject.category}
                                  </span>
                                </div>
                              </button>)}
                          </div> : <div className="px-4 py-8 text-center" data-id="element-2057">
                            <p className="text-sm text-text-secondary font-medium" data-id="element-2058">
                              No subjects found
                            </p>
                          </div>}
                      </div>
                    </div>}
                </div>

                {/* Sort Dropdown */}
                <div className="relative flex-1 lg:flex-initial lg:w-48" data-id="element-2059">
                  <select value={sortBy} onChange={e => setSortBy(e.target.value as 'popular' | 'recent' | 'title')} className="w-full px-3 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-[#00B75F] text-text-primary appearance-none cursor-pointer hover:border-[#00B75F] hover:border-opacity-50 transition-all shadow-sm" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M6 8L2 4h8z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center'
              }} data-id="element-2060">
                    <option value="popular" data-id="element-2061">Most Popular</option>
                    <option value="recent" data-id="element-2062">Most Recent</option>
                    <option value="title" data-id="element-2063">Title A-Z</option>
                  </select>
                </div>

                {/* Main Search Bar */}
                <div className="relative flex-1" data-id="element-2064">
                  <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary z-10" data-id="element-2065" />
                  <input type="text" placeholder="Search by title, creator, or tags..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-surface border-2 border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-[#00B75F] text-text-primary placeholder-text-tertiary hover:border-[#00B75F] hover:border-opacity-50 transition-all shadow-sm" data-id="element-2066" />
                </div>
              </div>
            </div>

            {/* Results Count */}
            <div className="mb-4" data-id="element-2067">
              <p className="text-sm text-text-secondary font-medium" data-id="element-2068">
                Showing {filteredAssessments.length} of{' '}
                {assessmentsByVisibility.length} assessments
                {getActiveFiltersText()}
              </p>
            </div>

            {/* Grid View Only */}
            {filteredAssessments.length === 0 ? <EmptyState icon={activeTab === 'public' ? <GlobeIcon size={48} className="text-text-tertiary" data-id="element-2070" /> : <BuildingIcon size={48} className="text-text-tertiary" data-id="element-2071" />} title="No assessments found" description="Try adjusting your filters or search query" actionLabel="Clear Filters" onAction={() => {
          setSearchQuery('');
          setSelectedSubject('');
          setSubjectInputValue('');
          setSelectedGradeLevel('all');
        }} data-id="element-2069" /> : <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-2072">
                {filteredAssessments.map(assessment => <PublicAssessmentCard key={assessment.id} assessment={assessment} onPreview={setPreviewAssessment} data-id="element-2073" />)}
              </div>}

            {/* Load More Button */}
            {filteredAssessments.length > 0 && <div className="flex justify-center mt-10" data-id="element-2074">
                <button className="text-[#2563EB] font-semibold border-2 border-[#2563EB] bg-surface-elevated hover:bg-[#2563EB] hover:text-white rounded-xl px-6 py-3 text-sm transition-all duration-200 shadow-sm hover:shadow-blue-glow" data-id="element-2075">
                  Load More
                </button>
              </div>}
          </motion.div>}
      </AnimatePresence>

      {/* Preview Modal */}
      {previewAssessment && <PreviewModal assessment={previewAssessment} onClose={() => setPreviewAssessment(null)} userRole="teacher" isSaved={savedAssessmentIds.has(previewAssessment.id)} onToggleSave={handleToggleSave} assignments={assessmentAssignments[previewAssessment.id] || []} onOpenAssignmentsPanel={autoCreate => {
      setAssignmentsPanelAssessment({
        ...previewAssessment,
        assignments: assessmentAssignments[previewAssessment.id] || []
      });
      setAutoCreateNew(autoCreate);
      setPreviewAssessment(null);
    }} data-id="element-2076" />}

      {/* Assignments Panel */}
      <AssignmentsPanel isOpen={!!assignmentsPanelAssessment} onClose={() => {
      setAssignmentsPanelAssessment(null);
      setAutoCreateNew(false);
    }} assessment={assignmentsPanelAssessment} onUpdateAssignments={handleUpdateAssignments} autoCreateNew={autoCreateNew} data-id="element-2077" />
    </section>;
};
export default PublicAssessments;